/**
 * All Access Pricing Configurator (Admin)
 * — View bundle sum + auto-suggested price
 * — Toggle between manual override and auto-scale formula
 * — Tune multipliers (basis points) for INR/USD independently
 *
 * Note: Effective price flows through backend `calculate_all_access_price()`,
 * so changing config here updates the live Pricing page + checkout immediately.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { RefreshCw, Crown, Calculator, Save, Wand2 } from 'lucide-react';
import { API_BASE, getAdminToken } from './adminUtils';

const bpsToPct = (bps) => (bps / 100).toFixed(2);

export const AllAccessPricingCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Editable state
  const [mode, setMode] = useState('manual');
  const [inrRupees, setInrRupees] = useState('');
  const [usdDollars, setUsdDollars] = useState('');
  const [inrBps, setInrBps] = useState('');
  const [usdBps, setUsdBps] = useState('');

  const load = useCallback(async () => {
    const token = getAdminToken(); if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/all-access-pricing-config`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const d = await res.json();
        setData(d);
        setMode(d.config.mode);
        setInrRupees(String(d.config.inr_rupees));
        setUsdDollars(String(d.config.usd_dollars));
        setInrBps(String(d.config.multiplier_inr_bps));
        setUsdBps(String(d.config.multiplier_usd_bps));
      } else {
        toast.error('Failed to load pricing config');
      }
    } catch (e) {
      toast.error('Network error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const token = getAdminToken(); if (!token) return;
    setSaving(true);
    try {
      const payload = {
        mode,
        inr_rupees: Number(inrRupees),
        usd_dollars: Number(usdDollars),
        multiplier_inr_bps: Number(inrBps),
        multiplier_usd_bps: Number(usdBps),
      };
      const res = await fetch(`${API_BASE}/api/admin/all-access-pricing-config`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast.success('All Access pricing saved');
        await load();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Save failed');
      }
    } catch (e) {
      toast.error('Network error');
    } finally {
      setSaving(false);
    }
  };

  const applySuggested = () => {
    if (!data) return;
    setInrRupees(String(data.auto_suggested.inr_rupees));
    setUsdDollars(String(data.auto_suggested.usd_dollars));
    toast.info('Suggested prices loaded — click Save to apply');
  };

  if (loading && !data) {
    return (
      <Card className="bg-white/5 border-white/10 backdrop-blur">
        <CardContent className="flex justify-center py-12">
          <RefreshCw className="w-8 h-8 text-amber-500 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const eff = data.effective;
  const bundle = data.bundle;
  const auto = data.auto_suggested;

  return (
    <Card className="bg-white/5 border-white/10 backdrop-blur" data-testid="all-access-pricing-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-white flex items-center gap-2">
            <Crown className="w-5 h-5 text-amber-400" />
            All Access Pricing Configurator
          </CardTitle>
          <Button variant="outline" size="icon" onClick={load} className="border-white/20 text-white">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        <p className="text-xs text-slate-400 mt-1">
          Manual mode keeps fixed prices. Auto mode = round(sum_of_bundle_skus × multiplier) — price auto-scales as you add new courses.
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Effective summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="bg-gradient-to-br from-amber-500/20 to-orange-600/10 border border-amber-500/20 rounded-xl p-4" data-testid="effective-inr-card">
            <p className="text-xs text-amber-300 uppercase tracking-wider">Effective INR</p>
            <p className="text-2xl font-bold text-white mt-1">₹{eff.inr_rupees.toLocaleString('en-IN')}</p>
            <p className="text-[10px] text-amber-400 mt-0.5">Live on /pricing</p>
          </div>
          <div className="bg-gradient-to-br from-blue-500/20 to-indigo-600/10 border border-blue-500/20 rounded-xl p-4" data-testid="effective-usd-card">
            <p className="text-xs text-blue-300 uppercase tracking-wider">Effective USD</p>
            <p className="text-2xl font-bold text-white mt-1">${eff.usd_dollars}</p>
            <p className="text-[10px] text-blue-400 mt-0.5">International checkout</p>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-xl p-4">
            <p className="text-xs text-slate-400 uppercase tracking-wider">Bundle Sum</p>
            <p className="text-2xl font-bold text-white mt-1">₹{bundle.sum_inr_rupees.toLocaleString('en-IN')}</p>
            <p className="text-[10px] text-slate-500 mt-0.5">${bundle.sum_usd} · {bundle.sku_count} SKUs</p>
          </div>
          <div className="bg-gradient-to-br from-emerald-500/15 to-teal-600/5 border border-emerald-500/20 rounded-xl p-4">
            <p className="text-xs text-emerald-300 uppercase tracking-wider flex items-center gap-1"><Calculator className="w-3 h-3" /> Auto-suggested</p>
            <p className="text-2xl font-bold text-white mt-1">₹{auto.inr_rupees.toLocaleString('en-IN')}</p>
            <p className="text-[10px] text-emerald-400 mt-0.5">${auto.usd_dollars} at current multipliers</p>
          </div>
        </div>

        {/* Mode toggle */}
        <div>
          <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Pricing Mode</p>
          <div className="flex gap-2">
            <button
              type="button"
              data-testid="mode-manual"
              onClick={() => setMode('manual')}
              className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                mode === 'manual'
                  ? 'bg-amber-500 text-white shadow-lg shadow-amber-500/30'
                  : 'bg-white/5 text-slate-300 border border-white/10 hover:bg-white/10'
              }`}
            >
              🔒 Manual (fixed prices)
            </button>
            <button
              type="button"
              data-testid="mode-auto"
              onClick={() => setMode('auto')}
              className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                mode === 'auto'
                  ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/30'
                  : 'bg-white/5 text-slate-300 border border-white/10 hover:bg-white/10'
              }`}
            >
              ⚡ Auto-scale formula
            </button>
          </div>
        </div>

        {/* Inputs */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className={mode === 'manual' ? '' : 'opacity-50 pointer-events-none'}>
            <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Manual prices</p>
            <div className="space-y-2">
              <div>
                <label className="text-xs text-slate-400">INR (₹)</label>
                <Input
                  data-testid="inr-rupees-input"
                  type="number"
                  value={inrRupees}
                  onChange={(e) => setInrRupees(e.target.value)}
                  className="bg-white/5 border-white/10 text-white"
                  min="100" step="100"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400">USD ($)</label>
                <Input
                  data-testid="usd-dollars-input"
                  type="number"
                  value={usdDollars}
                  onChange={(e) => setUsdDollars(e.target.value)}
                  className="bg-white/5 border-white/10 text-white"
                  min="1" step="1"
                />
              </div>
            </div>
          </div>
          <div className={mode === 'auto' ? '' : 'opacity-50 pointer-events-none'}>
            <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">
              Auto-scale multipliers <span className="text-slate-500 normal-case">(basis points, 100 = 1 %)</span>
            </p>
            <div className="space-y-2">
              <div>
                <label className="text-xs text-slate-400">INR multiplier — currently {bpsToPct(Number(inrBps) || 0)}%</label>
                <Input
                  data-testid="inr-bps-input"
                  type="number"
                  value={inrBps}
                  onChange={(e) => setInrBps(e.target.value)}
                  className="bg-white/5 border-white/10 text-white"
                  min="100" max="10000" step="10"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400">USD multiplier — currently {bpsToPct(Number(usdBps) || 0)}%</label>
                <Input
                  data-testid="usd-bps-input"
                  type="number"
                  value={usdBps}
                  onChange={(e) => setUsdBps(e.target.value)}
                  className="bg-white/5 border-white/10 text-white"
                  min="100" max="10000" step="10"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2 pt-2 border-t border-white/10">
          <Button
            onClick={save}
            disabled={saving}
            data-testid="save-pricing-btn"
            className="bg-amber-500 hover:bg-amber-600 text-white"
          >
            <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving…' : 'Save'}
          </Button>
          <Button
            variant="outline"
            onClick={applySuggested}
            data-testid="apply-suggested-btn"
            className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
          >
            <Wand2 className="w-4 h-4 mr-1" /> Apply auto-suggested
          </Button>
          {data.config.updated_at && (
            <p className="text-[11px] text-slate-500 ml-auto self-center" data-testid="last-updated">
              Last updated {new Date(data.config.updated_at).toLocaleString()} · by {data.config.updated_by || 'unknown'}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default AllAccessPricingCard;
